'use client';

import * as React from 'react';
import { Label } from './label';
import { cn } from './lib/utils';

type FieldLabelProps = React.ComponentPropsWithoutRef<typeof Label>;

function isLabelable(el: HTMLElement) {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLButtonElement
  );
}

/**
 * Label on its own line above a field, matching the AmpliFI Add Dealership
 * Owner step (14px medium). `htmlFor` focuses a native control. For a div
 * trigger, the click handler focuses it; give that trigger `aria-labelledby`
 * set to this label's id (`label-${htmlFor}` unless `id` is passed).
 */
export const FieldLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  FieldLabelProps
>(({ className, htmlFor, id, onClick, ...props }, ref) => {
  const labelId = id ?? (htmlFor ? `label-${htmlFor}` : undefined);

  return (
    <Label
      ref={ref}
      id={labelId}
      htmlFor={htmlFor}
      className={cn('mb-2 block', className)}
      onClick={(event) => {
        onClick?.(event);
        if (!htmlFor || event.defaultPrevented) return;
        const el = document.getElementById(htmlFor);
        if (!el || isLabelable(el)) return;
        const slider = el.querySelector<HTMLElement>('[role="slider"]');
        (slider ?? el).focus();
      }}
      {...props}
    />
  );
});
FieldLabel.displayName = 'FieldLabel';
